import {
  CustomerApi,
  CustomerApiEndpoints,
} from "@/config/api/customer/customerApi";
import { executeApiRequest } from "@/services/api/apiService";
import { useToastErrorHandler } from "@/composables/useToastErrorHandler";
import type {
  InvoiceRecipient,
  Organization,
  CustomerIntegration,
} from "@/stores/tender/types";

const useCustomerApiService = () => {
  const { withToastErrorHandling } = useToastErrorHandler();

  const getCustomerDetailsForGivenPriceRequest = async (
    supplierId: number,
    priceRequestId: number,
  ) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<unknown>(
          {
            endpointKey: Symbol.for(
              CustomerApiEndpoints.GET_CUSTOMER_DETAILS_FOR_GIVEN_PRICE_REQUEST,
            ),
            config: CustomerApi,
          },
          {
            pathParams: [supplierId, "request", priceRequestId, "details"],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Kundefeil",
          detail: "Kunne ikke hente kundedetaljer for prisforespørselen",
        },
      },
    );
  };

  const getPropertyInvoiceRecipient = async (params: {
    orgNumber: string;
    customerPropertyId: number;
  }) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<InvoiceRecipient>(
          {
            endpointKey: Symbol.for(
              CustomerApiEndpoints.GET_PROPERTY_INVOICE_RECIPIENT,
            ),
            config: CustomerApi,
          },
          {
            pathParams: [
              params.orgNumber,
              "property",
              params.customerPropertyId,
              "invoiceRecipient",
            ],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Fakturadatafeil",
          detail: "Kunne ikke hente informasjon om fakturamottaker for eiendom",
        },
      },
    );
  };

  const getOrganizationInvoiceRecipient = async (params: {
    orgNumber: string;
  }) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<InvoiceRecipient>(
          {
            endpointKey: Symbol.for(
              CustomerApiEndpoints.GET_ORGANIZATION_INVOICE_RECIPIENT,
            ),
            config: CustomerApi,
          },
          {
            pathParams: [params.orgNumber, "invoiceRecipient"],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Fakturadatafeil",
          detail:
            "Kunne ikke hente informasjon om fakturamottaker for organisasjon",
        },
      },
    );
  };

  const getOrganizationsFromOrgNumbers = async (orgNumber: string) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<Organization[]>(
          {
            endpointKey: Symbol.for(
              CustomerApiEndpoints.GET_ORGANIZATIONS_FROM_ORG_NUMBERS,
            ),
            config: CustomerApi,
          },
          {
            pathParams: [orgNumber],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Organisasjonsfeil",
          detail: "Kunne ikke hente organisasjonsinformasjon",
        },
      },
    );
  };

  const getCustomerIntegrations = async (orgNumber: string) => {
    return withToastErrorHandling(
      async () => {
        return executeApiRequest<CustomerIntegration[]>(
          {
            endpointKey: Symbol.for(
              CustomerApiEndpoints.GET_CUSTOMER_INTEGRATIONS,
            ),
            config: CustomerApi,
          },
          {
            pathParams: [orgNumber, "CustomerIntegration"],
          },
        );
      },
      {
        errorOptions: {
          severity: "warn",
          summary: "Integrasjonsfeil",
          detail: "Kunne ikke hente kunde-integrasjoner",
        },
      },
    );
  };

  return {
    getCustomerDetailsForGivenPriceRequest,
    getPropertyInvoiceRecipient,
    getOrganizationInvoiceRecipient,
    getOrganizationsFromOrgNumbers,
    getCustomerIntegrations,
  };
};

export const useCustomerApi = useCustomerApiService;
